import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import useDownload from "../../../hooks/useDownload";
import {MasterClassService} from "../../../services/masterClassService/masterClassService";
import Loader from "../../../components/common/Loader";
import avatarUser from "../../../assets/avatarUser.png";
import MasterClassLessonsList from "./MasterClassLessonsList";
import notFound from "../../../assets/not-found/not_found.png";
import DiscussionsCommentList from "../../../components/DiscussionsCommentList";
import useRenderTextEditorContent from "../../../hooks/useTextEditorContent";
import {PageErrorMessage} from "../../../components/PageErrorMessage";
import UserDetails from "../../../components/UserDetails";
import TextEditorContent from "../../../components/TextEditorContent";
import Tags from "../../../components/Tags/Tags";
import SpeakerBio from "./SpeakerBio";
import CustomBreadcrumbs from "../../../components/CustomBreadcrumbs/CustomBreadcrumbs";

const SpeakerMasterClassDetails = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [masterClassDetails, setMasterClassDetails] = useState(null);
    const { id } = useParams();
    const downloadFile = useDownload();
    const navigate = useNavigate();
    const renderAnswer = useRenderTextEditorContent();
    const [error, setError] = useState(null);

    const masterClassDetailsData = async () => {
        setIsLoading(true);
        try {
            await MasterClassService.getSpeakerMasterClassData(id).then((response) => {
                setMasterClassDetails(response?.data);
            });
            setIsLoading(false);
        } catch (error) {
            setError({
                statusCode: error.response.status,
                message: error.response.data.message,
            });
            setIsLoading(false);
        }
    };

    // useEffect of call all api function
    useEffect(() => {
        masterClassDetailsData();
    }, [id]);

    const handleDownloadPlaybook = async () => {
        if (masterClassDetails.playbook_pdf) {
            const filename = (masterClassDetails?.name || "playbook_file").replace(/[^\w\s]/g, "_").replace(/\s+/g, "_");
            await downloadFile(masterClassDetails.playbook_pdf, filename);
            await MasterClassService.playbookDownloadLog(id);
        }
    };

    if (error) {
        return <PageErrorMessage
            error={error}
            entityTitle="Masterclass"
        />;
    }

    const pages = masterClassDetails?.intersected_programs.length === 1
        ? [{
            title: masterClassDetails?.intersected_programs[0].title,
            to: `/programs/${masterClassDetails?.intersected_programs[0].id}`
        }]
        : [{
            title: 'Back to Program',
            isMultiple: true,
            items: masterClassDetails?.intersected_programs.map((program) => ({
                title: program.title,
                to: `/programs/${program.id}`
            }))
        }];

    return (
        <>
            {isLoading ? (
                <>
                    <Loader />
                </>
            ) : (
                <>
                    <div className="container m-4">
                        <div className="row">
                            {masterClassDetails?.intersected_programs.length !== 0 &&
                                <CustomBreadcrumbs
                                    currentPageTitle={masterClassDetails?.name}
                                    pages={pages}
                                />
                            }
                        </div>
                        <div className="row mb-5">
                            <div className="col-md-6 mb-4 mb-md-0">
                                <h1>{masterClassDetails?.name}</h1>
                                {masterClassDetails?.tags.length > 0 &&
                                    <div className="mt-2 mb-2">
                                        <Tags
                                            tags={masterClassDetails?.tags}
                                        />
                                    </div>
                                }
                                <TextEditorContent
                                    text={masterClassDetails?.description}
                                />
                                <div className="d-flex mt-3">
                                    {masterClassDetails?.playbook_pdf &&
                                        <button className="btn btn-primary me-2"
                                                disabled={!masterClassDetails?.playbook_pdf}
                                                onClick={handleDownloadPlaybook}>Download Playbook</button>
                                    }
                                    <button
                                        className="btn btn-primary-info"
                                        onClick={() => navigate('/lessons/' + masterClassDetails?.first_lesson)}
                                        disabled={masterClassDetails?.first_lesson === null}
                                    >
                                        Start Watching
                                    </button>
                                </div>
                                <div className="mt-3">
                                    {masterClassDetails?.lessons.length > 0 &&
                                        <MasterClassLessonsList
                                            lessons={masterClassDetails?.lessons}
                                            withProgress={false}
                                        />
                                    }
                                    {masterClassDetails?.lessons.length === 0 &&
                                        <div
                                            className="w-100 mt-3 rounded-4 text-center d-flex flex-column align-items-center justify-content-center"
                                            style={{height: "500px"}}>
                                            <img crossOrigin="anonymous" src={notFound} alt="data not found" width={300}/>
                                            <h3>No Lessons Added</h3>
                                            <p className="fs-5">No lessons available at the moment.</p>
                                        </div>
                                    }
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="master-class__image">
                                    <img
                                        src={masterClassDetails?.cover_image}
                                        alt={masterClassDetails?.name}
                                        className="img-fluid"
                                    />
                                </div>
                                <div className="master-class__speaker-info p-3 mt-3">
                                    <div className="d-flex justify-content-center align-items-center flex-column">
                                        <div onClick={() => navigate(`/community/users/${masterClassDetails?.speaker.id}`)}>
                                            <UserDetails
                                                user={masterClassDetails?.speaker}
                                            />
                                        </div>
                                        <div className="mt-1">
                                            {masterClassDetails?.speaker?.bio && <SpeakerBio bio={masterClassDetails.speaker.bio} />}
                                        </div>
                                    </div>
                                </div>
                                <div className="master-class__comments mt-3">
                                    <DiscussionsCommentList
                                        path={`master-classes/${masterClassDetails?.id}`}
                                        relatedTitlesToDisplay={['lessons']}
                                        highlightingCompareId={masterClassDetails?.speaker.id}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default SpeakerMasterClassDetails;